if (!customElements.get('alma-widget')) {
  class AlmaWidget extends HTMLElement {
    static SELECTORS = { container: '.alma-widget-box', summary: 'multi-add-summary' };
    static EVENTS = { summaryChange: 'multi-add-summary:change', variantChange: 'variant:change' };
    static MODES = { live: 'live' };
    static DEFAULT_LOCALE = 'fr';
    static LIBRARY_POLL_MS = 200;
    static PLANS = [
      { installmentsCount: 2, minAmount: 9900, maxAmount: 500000 },
      { installmentsCount: 3, minAmount: 9900, maxAmount: 500000 },
      { installmentsCount: 4, minAmount: 40000, maxAmount: 500000 },
      { installmentsCount: 10, minAmount: 40000, maxAmount: 500000 }
    ];

    connectedCallback () {
      this._abort = new AbortController();
      this._apiKey = this.dataset.apiKey || '';
      this._isLive = this.dataset.mode === AlmaWidget.MODES.live;
      this._formId = this.dataset.formId || null;
      this._amount = this._toInt(this.dataset.price, 0);
      this._containerEl = this.querySelector(AlmaWidget.SELECTORS.container);
      this._summaryEl = this._findSummary();
      this._widgets = null;
      this._libraryReady = false;

      if (!this._apiKey || !this._containerEl?.id) {
        console.warn('AlmaWidget: missing API key or container id.');
        return;
      }

      this._listen();
      this._seedFromSummary();
      this._whenLibraryReady().then(() => {
        this._libraryReady = true;
        this._render();
      });
    }

    disconnectedCallback () {
      this._abort?.abort();
      clearInterval(this._libraryPoll);
    }

    /* ── Price sources ── */

    // The summary already computes what the customer pays (variant + offers + packs);
    // raw variant:change is only the fallback when no summary exists on the page.
    _listen () {
      const { signal } = this._abort;
      document.addEventListener(AlmaWidget.EVENTS.summaryChange, (event) => this._onSummaryChange(event), { signal });
      if (!this._summaryEl) {
        document.addEventListener(AlmaWidget.EVENTS.variantChange, (event) => this._onVariantChange(event), { signal });
      }
    }

    _findSummary () {
      const scoped = this._formId
        ? document.querySelector(`${AlmaWidget.SELECTORS.summary}[form="${this._formId}"]`)
        : null;
      return scoped || document.querySelector(AlmaWidget.SELECTORS.summary);
    }

    _seedFromSummary () {
      if (!this._summaryEl) return;
      customElements.whenDefined(AlmaWidget.SELECTORS.summary).then(() => {
        if (this._abort.signal.aborted) return;
        this._setAmount(this._summaryEl.getTotal?.()?.total);
      });
    }

    _onSummaryChange (event) {
      const { total, formId } = event.detail || {};
      if (this._formId && formId && formId !== this._formId) return;
      this._setAmount(total);
    }

    _onVariantChange (event) {
      const { variant, formId } = event.detail || {};
      if (this._formId && formId && formId !== this._formId) return;
      this._setAmount(variant?.price);
    }

    _setAmount (cents) {
      const amount = this._toInt(cents, 0);
      if (amount <= 0 || amount === this._amount) return;
      this._amount = amount;
      this._render();
    }

    /* ── Rendering ── */

    _whenLibraryReady () {
      return new Promise((resolve) => {
        this._libraryPoll = setInterval(() => {
          if (this._abort.signal.aborted) return clearInterval(this._libraryPoll);
          if (!window.Alma?.Widgets) return;
          clearInterval(this._libraryPoll);
          resolve();
        }, AlmaWidget.LIBRARY_POLL_MS);
      });
    }

    // widgets.add() unmounts whatever is in the container first, so re-calling it is the update path.
    _render () {
      if (!this._libraryReady || this._amount <= 0) return;
      try {
        const Alma = window.Alma;
        this._widgets ||= Alma.Widgets.initialize(this._apiKey, this._isLive ? Alma.ApiMode.LIVE : Alma.ApiMode.TEST);
        this._widgets.add(Alma.Widgets.PaymentPlans, {
          container: `#${this._containerEl.id}`,
          locale: this._locale(),
          hideIfNotEligible: true,
          purchaseAmount: this._amount,
          monochrome: false,
          plans: AlmaWidget.PLANS
        });
      } catch (error) {
        console.error('AlmaWidget: failed to render widget', error);
      }
    }

    _locale () {
      return (document.documentElement.lang || AlmaWidget.DEFAULT_LOCALE).split('-')[0];
    }

    _toInt (value, fallback) {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : fallback;
    }
  }

  customElements.define('alma-widget', AlmaWidget);
}
